import {
  ClipboardDocumentListIcon,
  DocumentCheckIcon,
  DocumentIcon,
  DocumentTextIcon,
  MapPinIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import { Button } from "@/components/ui/Button";
import type { Montageunterlage } from "@/lib/projectOverview";
import type { Stueckliste } from "@/lib/stuecklisten";
import {
  buildUnterlagenPreview,
  unterlagenCountChips,
  type UnterlagenPackKind,
  type UnterlagenPreviewKind,
} from "@/projectOverview/unterlagenPreview";

const KIND_ICON: Record<UnterlagenPreviewKind, typeof DocumentIcon> = {
  file: DocumentIcon,
  note: DocumentTextIcon,
  address: MapPinIcon,
  stueckliste: ClipboardDocumentListIcon,
  ab: DocumentCheckIcon,
};

export function UnterlagenPackCard({
  pack,
  items,
  stuecklisten,
  hasAb = false,
  lead = null,
  loading,
  canManage,
  onOpen,
}: {
  pack: UnterlagenPackKind;
  items: Montageunterlage[] | null;
  stuecklisten: Stueckliste[];
  hasAb?: boolean;
  lead?: { title: string; subtitle?: string; tabKey?: string | null } | null;
  loading: boolean;
  canManage: boolean;
  /** null öffnet den Standardreiter (Deckblatt). */
  onOpen: (tabKey: string | null) => void;
}) {
  const loaded = items ?? [];
  const preview = buildUnterlagenPreview(loaded, stuecklisten, pack, { hasAb });
  const chips = unterlagenCountChips(preview.counts);
  const empty =
    !loading &&
    preview.counts.files + preview.counts.notes + preview.counts.addresses + preview.counts.stuecklisten === 0 &&
    !preview.counts.hasAb &&
    !lead;
  const title =
    pack === "werkstatt" ? "Werkstatt" : pack === "aufmass" ? "Aufmaß" : "Montage";
  const emptyHint =
    pack === "werkstatt"
      ? "Pläne, Notizen und Stücklisten für die Fertigung"
      : pack === "aufmass"
        ? "Brandschutztüren-Aufmaß und Notizen für den Termin"
        : "Pläne, Adressen und Stücklisten für die Baustelle";

  return (
    <div className="eh-po-unterlagen-pack">
      <div className="eh-po-unterlagen-pack-head">
        <div className="eh-po-unterlagen-pack-head-row">
          <h4 className="eh-po-unterlagen-pack-title">{title}</h4>
          <Button
            type="button"
            size="sm"
            variant={empty ? "primary" : "secondary"}
            leftIcon={empty && canManage ? <PlusIcon className="h-4 w-4" /> : undefined}
            onClick={() => onOpen(null)}
          >
            {canManage ? (empty ? "Hinzufügen" : "Öffnen") : "Anzeigen"}
          </Button>
        </div>
        <p className="eh-po-unterlagen-pack-sub">
          {loading
            ? "Wird geladen …"
            : chips.length
              ? chips.join(" · ")
              : lead
                ? lead.subtitle || lead.title
                : "Noch nichts hinterlegt"}
        </p>
      </div>

      {loading ? (
        <div className="eh-po-unterlagen-skel">
          <div className="h-7 animate-pulse rounded-lg bg-fill" />
          <div className="h-7 animate-pulse rounded-lg bg-fill" />
        </div>
      ) : empty ? (
        <button type="button" className="eh-po-unterlagen-empty" onClick={() => onOpen(null)}>
          <span className="text-sm font-medium text-label">Noch keine {title}unterlagen</span>
          <span className="text-xs text-label-tertiary">{emptyHint}</span>
        </button>
      ) : (
        <ul className="eh-po-unterlagen-list">
          {lead ? (
            <li>
              <button type="button" className="eh-po-unterlagen-item" onClick={() => onOpen(lead.tabKey ?? null)}>
                <span className="eh-po-unterlagen-item-icon" aria-hidden>
                  <ClipboardDocumentListIcon className="h-4 w-4" />
                </span>
                <span className="min-w-0 flex-1 text-left">
                  <span className="eh-po-unterlagen-item-title">{lead.title}</span>
                  {lead.subtitle ? <span className="eh-po-unterlagen-item-sub">{lead.subtitle}</span> : null}
                </span>
              </button>
            </li>
          ) : null}
          {preview.items.map((item) => {
            const Icon = KIND_ICON[item.kind];
            return (
              <li key={item.key}>
                <button type="button" className="eh-po-unterlagen-item" onClick={() => onOpen(item.tabKey)}>
                  <span className="eh-po-unterlagen-item-icon" aria-hidden>
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="min-w-0 flex-1 text-left">
                    <span className="eh-po-unterlagen-item-title">{item.title}</span>
                    {item.subtitle ? (
                      <span className="eh-po-unterlagen-item-sub">{item.subtitle}</span>
                    ) : null}
                  </span>
                </button>
              </li>
            );
          })}
          {preview.hiddenCount > 0 ? (
            <li>
              <button type="button" className="eh-po-unterlagen-more" onClick={() => onOpen(null)}>
                +{preview.hiddenCount} weitere
              </button>
            </li>
          ) : null}
        </ul>
      )}
    </div>
  );
}
